// ── Viewer (Phase 2B — placeholder implementation) ──
//
// Viewer is the Field's attention pattern, NOT a creature. The locked design
// (see docs/BEATS-TIMELINE.md "Viewer brief", May 4 2026) is:
//   "code-only field attention aperture with filamentary sensing vectors"
// A dark central aperture surrounded by hundreds of fine, hairline vectors
// that radiate outward (iris) or converge inward toward Bit (Bit metabolised
// by the Field). Reads as awareness forming inside the Field.
//
// Motion language: electrical, magnetic, or mycelial. Lines stretch, bend,
// split, rejoin, converge. They do NOT wiggle like cartoon tentacles.
// Three layered timescales: fast micro-jitter, slow macro-breathing, sudden
// alignment on Bit entry.
//
// Current implementation: 8 spring-damped "tentacle" tips ported from
// curious-dusts WGSL — a placeholder that gets the alpha/stage state machine
// right but reads animal/octopus, not field-phenomenon. Refactor pending:
// (a) replace 8 tentacles with hundreds of hairline vectors emanating from a
//     dark central aperture; (b) implement Radiate vs Converge modes with
//     mode-blend driven by Bit proximity; (c) rename `tentacle*` identifiers
//     to `filament*` / `vector*` once the refactor lands.
//
// External naming: NEVER. Per B1, Viewer is not named in DOM, alt, meta, or
// any user-visible surface. Code-only term.
//
// Stages mapped to glassProgress (current placeholder):
//   latent      < 0.20            invisible (substrate only)
//   forming     0.20 → 0.40       length grows 0 → rest, alpha 0 → 1
//   addressable 0.40 → 0.85       full presence, sway active
//   fadeout     0.85 → 1.00       alpha fades to 0
//
// Anchor is the field-centre (50%, 70%) — same point where FieldBit lands at
// peak descent. Co-location at Build is the "shared building motion" (D24).

import { ILLUMINATION } from '../lib/illumination-params.js';
import { smoothstep } from '../lib/easing.js';
import { noise2d } from '../lib/noise.js';

interface Tentacle {
  angle: number;       // radial rest angle
  restLength: number;  // px at full extension
  tipX: number;
  tipY: number;
  velX: number;
  velY: number;
  phase: number;       // per-tentacle oscillation phase
}

export class Viewer {
  private tentacles: Tentacle[] = [];
  private anchorX: number = 0;
  private anchorY: number = 0;
  private fieldLifeTime: number = 0;

  // Physics constants (curious-dusts/src/config.ts: ftSpring/ftDamping/ftSwayAmp)
  private readonly SPRING = 2.5;
  private readonly DAMPING = 3.1;
  private readonly SWAY_AMP = 0.6;

  constructor(count: number = 8) {
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const lengthJitter = 0.85 + Math.random() * 0.30; // 0.85..1.15
      this.tentacles.push({
        angle,
        restLength: ILLUMINATION.viewerTentacleLength * lengthJitter,
        tipX: 0,
        tipY: 0,
        velX: 0,
        velY: 0,
        phase: i * 0.7,
      });
    }
  }

  setAnchor(x: number, y: number): void {
    this.anchorX = x;
    this.anchorY = y;
    // Initialise tip positions on first anchor set so spring doesn't snap.
    if (this.tentacles[0]?.tipX === 0 && this.tentacles[0]?.tipY === 0) {
      for (const t of this.tentacles) {
        t.tipX = x + Math.cos(t.angle) * t.restLength * 0.3;
        t.tipY = y + Math.sin(t.angle) * t.restLength * 0.3;
      }
    }
  }

  /** Step physics. dt clamped externally. */
  update(dt: number, fieldLifeTime: number, lengthFactor: number): void {
    this.fieldLifeTime = fieldLifeTime;
    if (lengthFactor < 0.01) return;

    const slowT = fieldLifeTime * 0.12;

    for (const t of this.tentacles) {
      const len = t.restLength * lengthFactor;

      // Noise sway (mirrors curious-dusts tentacle-sim.wgsl gradNoise terms).
      const swayX =
        noise2d(this.anchorX * 0.005 + slowT, this.anchorY * 0.005 + 33.0)
        * this.SWAY_AMP * len * 0.6;
      const swayY =
        noise2d(this.anchorX * 0.005 + 77.0, this.anchorY * 0.005 + slowT)
        * this.SWAY_AMP * len * 0.6;

      // Per-tentacle micro-oscillation.
      const oscX = Math.sin(fieldLifeTime * 1.2 + t.phase) * len * 0.08;
      const oscY = Math.cos(fieldLifeTime * 0.9 + t.phase * 1.3) * len * 0.06;

      const targetX = this.anchorX + Math.cos(t.angle) * len + swayX + oscX;
      const targetY = this.anchorY + Math.sin(t.angle) * len + swayY + oscY;

      const dx = targetX - t.tipX;
      const dy = targetY - t.tipY;
      t.velX += dx * this.SPRING * dt;
      t.velY += dy * this.SPRING * dt;
      t.velX *= Math.max(0, 1 - this.DAMPING * dt);
      t.velY *= Math.max(0, 1 - this.DAMPING * dt);
      t.tipX += t.velX;
      t.tipY += t.velY;

      // Length clamp (curious-dusts: tDist > maxLen → snap back).
      const tdx = t.tipX - this.anchorX;
      const tdy = t.tipY - this.anchorY;
      const tDist = Math.hypot(tdx, tdy);
      const maxLen = len * 1.5;
      if (tDist > maxLen && tDist > 0) {
        t.tipX = this.anchorX + (tdx / tDist) * maxLen;
        t.tipY = this.anchorY + (tdy / tDist) * maxLen;
      }
    }
  }

  render(ctx: CanvasRenderingContext2D, alpha: number, latentAlpha: number = 0): void {
    if (alpha < 0.01 && latentAlpha < 0.01) return;

    const { r, g, b } = ILLUMINATION.baseColor;
    const widthPx = ILLUMINATION.viewerStrokeWidth;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineWidth = widthPx;

    if (alpha >= 0.01) {
      for (const t of this.tentacles) {
        const grad = ctx.createLinearGradient(this.anchorX, this.anchorY, t.tipX, t.tipY);
        grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.75})`);
        grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        ctx.strokeStyle = grad;
        ctx.beginPath();
        ctx.moveTo(this.anchorX, this.anchorY);
        ctx.lineTo(t.tipX, t.tipY);
        ctx.stroke();
      }
    }

    // Anchor glow — the "presence" itself. Visible during latent stage too so
    // attention has something to gather around BEFORE tentacles form (D23).
    const glowAlphaActive = alpha * 0.45;
    const glowAlpha = Math.max(glowAlphaActive, latentAlpha);
    if (glowAlpha > 0.005) {
      const glowR = ILLUMINATION.viewerAnchorGlow;
      const glow = ctx.createRadialGradient(
        this.anchorX, this.anchorY, 0,
        this.anchorX, this.anchorY, glowR,
      );
      glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${glowAlpha})`);
      glow.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.fillStyle = glow;
      ctx.fillRect(this.anchorX - glowR, this.anchorY - glowR, glowR * 2, glowR * 2);
    }

    ctx.restore();
  }
}

/** Compute alpha + length + latent factors for a given glassProgress. */
export function viewerStageFor(glassProgress: number): {
  alpha: number;        // tentacle visibility (0 during latent, ramps in 0.20→0.40)
  latentAlpha: number;  // anchor-glow visibility during latent (0..0.20)
  lengthFactor: number; // tentacle length scalar (0..1)
  stage: 'latent' | 'forming' | 'addressable' | 'fadeout';
} {
  const formIn = smoothstep(0.20, 0.40, glassProgress);
  const fadeOut = 1 - smoothstep(0.85, 1.00, glassProgress);
  const alpha = formIn * fadeOut;

  // Latent presence: faint anchor glow that fades in across Hero (gp 0..0.10),
  // peaks at ~0.10, then hands off to the formed Viewer by 0.20.
  const latentRamp = smoothstep(0.00, 0.08, glassProgress)
    * (1 - smoothstep(0.15, 0.20, glassProgress));
  const latentAlpha = latentRamp * 0.20;

  let stage: 'latent' | 'forming' | 'addressable' | 'fadeout' = 'latent';
  if (glassProgress < 0.20) stage = 'latent';
  else if (glassProgress < 0.40) stage = 'forming';
  else if (glassProgress < 0.85) stage = 'addressable';
  else stage = 'fadeout';

  return { alpha, latentAlpha, lengthFactor: formIn, stage };
}
